import { z } from 'zod'
import { uuid } from './common.js'

/**
 * Ana's library of prose — the paragraphs she writes for every trip.
 *
 * "How to get from CDG to the hotel", "how tipping works here", "what to do if
 * you lose your passport". She has written each of these a dozen times, slightly
 * differently every time, which is both an hour a trip and a reason the good
 * version only exists on whichever itinerary she happened to write it best on.
 *
 * Shaped after `savedPlaces` because it is the same idea about a different kind
 * of content, and it obeys the same rule: inserting one into a trip **copies**
 * the text. See PATTERNS §3c — nothing in the database points back here, so no
 * later edit can reach an itinerary somebody is already carrying.
 */

/**
 * What kind of paragraph it is.
 *
 * Coarse for the same reason `savedPlaceKind` is coarse: the job is to cut a
 * list of eighty down to something scannable, and a taxonomy fine enough to be
 * interesting is one nobody applies consistently twice. `other` is a real
 * answer.
 */
export const snippetCategory = z.enum([
  'arrival',
  'transport',
  'practical',
  'dining',
  'safety',
  'packing',
  'other',
])
export type SnippetCategory = z.infer<typeof snippetCategory>

const snippetFields = {
  /** How she recognises it in the list. Never shown to a traveller. */
  title: z.string().trim().min(1).max(200),
  category: snippetCategory.default('other'),
  /**
   * Where it applies, as she would say it — or nothing at all.
   *
   * Nullable, which is the one place this diverges from `savedPlaces`: a place
   * is always somewhere, but half of what Ana rewrites every trip is true of
   * every trip. "What to do if you lose your passport" is not a Paris fact, and
   * filing it under a city would bury it on the trip where she needs it.
   */
  destination: z.string().trim().max(120).nullish(),
  /**
   * The prose itself, as Markdown — it lands in a note and is rendered the same
   * way every other note is.
   *
   * Capped well below `MAX_NOTES_LENGTH` on purpose. A snippet is one block that
   * goes *into* a note alongside whatever else is there; one that could fill the
   * note on its own is a document, and a library of documents is a different
   * feature with different problems.
   */
  body: z.string().trim().min(1).max(8_000),
}

export const createSnippetRequest = z.object(snippetFields)
export type CreateSnippetRequest = z.infer<typeof createSnippetRequest>

export const updateSnippetRequest = z.object(snippetFields).partial()
export type UpdateSnippetRequest = z.infer<typeof updateSnippetRequest>

export const snippet = z.object({
  id: uuid,
  title: z.string(),
  category: snippetCategory,
  destination: z.string().nullable(),
  body: z.string(),
})
export type Snippet = z.infer<typeof snippet>

/**
 * Putting one into a day's note.
 *
 * There is no field saying where in the note it goes, and there should not be:
 * it is appended, and what was already written stays. The response is the whole
 * updated day, so the form Ana is looking at can show her the result rather than
 * guess at it.
 */
export const insertSnippetRequest = z.object({
  snippetId: uuid,
})
export type InsertSnippetRequest = z.infer<typeof insertSnippetRequest>
